import { formatClaimPayoutUsd, payoutPotentialUsd } from './payout';
import type { SettlementListItem } from '../types';

export type ClaimStatusKind =
  | 'activeSettlement'
  | 'noActionNeeded'
  | 'pendingArbitration'
  | 'potentialArbitration';

export type ClaimStatusTone = 'active' | 'pending' | 'potential';

/** iOS `ClaimsCardView.proofForeground` / `proofImageName`. */
export type ClaimProofTone = 'required' | 'positive' | 'unknown';

export interface QuizClaimCardView {
  company: string;
  iconUrl: string | null;
  statusTitle: string;
  statusTone: ClaimStatusTone;
  proof: string;
  proofTone: ClaimProofTone;
  deadline: string;
  audience: string;
  payoutTitle: string;
  payoutLabel: string;
}

const PAYOUT_LABEL_MAX = 20;
const PAYOUT_HAS_CURRENCY = /\$\s?\d/;

function claimStatusKind(badge: string | null): ClaimStatusKind {
  switch (badge) {
    case 'no_action_needed':
      return 'noActionNeeded';
    case 'coming_soon':
      return 'pendingArbitration';
    case 'closed':
      return 'potentialArbitration';
    default:
      return 'activeSettlement';
  }
}

function claimStatusTitle(kind: ClaimStatusKind): string {
  switch (kind) {
    case 'activeSettlement':
      return 'Active settlement';
    case 'noActionNeeded':
      return 'Paid automatically';
    case 'pendingArbitration':
      return 'Pending arbitration';
    case 'potentialArbitration':
      return 'Potential arbitration';
  }
}

function statusTone(kind: ClaimStatusKind, formComingSoon: boolean): ClaimStatusTone {
  if (formComingSoon) return 'potential';
  switch (kind) {
    case 'activeSettlement':
    case 'noActionNeeded':
      return 'active';
    case 'pendingArbitration':
      return 'pending';
    case 'potentialArbitration':
      return 'potential';
  }
}

/** iOS `Claim.getProofRequirement` */
export function claimProofRequirement(item: SettlementListItem): string {
  if (item.claim_required === false) return 'No action needed';
  switch ((item.display.proof_label ?? '').toLowerCase()) {
    case 'yes':
      return 'Proof required';
    case 'no':
      return 'No proof needed';
    default:
      return 'Proof unknown';
  }
}

/** iOS `ClaimsCardView` proof color/icon: uses `proofRequired` bool, not the label. */
export function claimProofTone(item: SettlementListItem): ClaimProofTone {
  if (item.claim_required !== false && item.proof_required === true) return 'required';
  if (item.claim_required === false || item.proof_required === false) return 'positive';
  return 'unknown';
}

/** iOS `ClaimsViewModel.getPayoutLabel` fallback when no USD amount is known. */
export function claimDisplayPayoutLabel(label: string | null): string {
  const payoutLabel = (label ?? '').trim();
  if (PAYOUT_HAS_CURRENCY.test(payoutLabel) && payoutLabel.length <= PAYOUT_LABEL_MAX) {
    return payoutLabel;
  }
  return 'Amount varies';
}

/** iOS `ClaimsViewModel.getPayoutLabel`. */
export function claimCardPayoutLabel(item: SettlementListItem): string {
  const potential = payoutPotentialUsd({
    maxUsd: item.payout.max_usd,
    noProofUsd: item.payout.no_proof_usd,
    withProofUsd: item.payout.with_proof_usd ?? null,
  });
  if (potential != null) return formatClaimPayoutUsd(potential);
  return claimDisplayPayoutLabel(item.display.payout_label);
}

function claimAudience(company: string | null | undefined): string {
  return company ? `Popular claim from ${company}` : 'Popular settlement';
}

/** Presentation for the quiz/onboarding claims picker — mirrors iOS `ClaimsCardView`. */
export function presentQuizClaimCard(item: SettlementListItem): QuizClaimCardView {
  const companyName = item.display.company || null;
  const kind = claimStatusKind(item.display.badge);
  const formComingSoon = item.claim_required !== false && item.filing_ready === false;

  return {
    company: companyName || item.name,
    iconUrl: item.display.icon_url ?? item.icon_url ?? null,
    statusTitle: formComingSoon ? 'Form coming soon' : claimStatusTitle(kind),
    statusTone: statusTone(kind, formComingSoon),
    proof: claimProofRequirement(item),
    proofTone: claimProofTone(item),
    deadline: item.display.deadline_label ?? '',
    audience: claimAudience(companyName),
    payoutTitle: item.claim_required === false ? 'Paid automatically' : 'Claim up to',
    payoutLabel: claimCardPayoutLabel(item),
  };
}
